import React, { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { formatPlaceAddress, loadGoogleMapsPlaces } from "../config/googleMaps";

/**
 * Google Places Autocomplete — businesses + addresses (Albania).
 * Omits `types` so both establishments and geocoded places appear.
 */
const PlaceAutocompleteInput = ({
  id,
  value,
  onChange,
  placeholder,
  className = "bt-input",
  required = false,
  "data-testid": testId,
}) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState(false);

  onChangeRef.current = onChange;

  useEffect(() => {
    let cancelled = false;
    loadGoogleMapsPlaces()
      .then(() => {
        if (!cancelled) setReady(true);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!ready || !inputRef.current || !window.google?.maps?.places) return;

    // No `types` filter → businesses (establishment) + addresses (geocode)
    const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
      componentRestrictions: { country: "al" },
      fields: ["formatted_address", "geometry", "name", "place_id", "types"],
      bounds: new window.google.maps.LatLngBounds(
        new window.google.maps.LatLng(39.6, 19.2),
        new window.google.maps.LatLng(42.7, 21.1)
      ),
      strictBounds: false,
    });

    const listener = autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      if (!place?.geometry?.location) return;
      const address = formatPlaceAddress(place);
      onChangeRef.current({
        address,
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
        place_id: place.place_id,
      });
    });

    autocompleteRef.current = autocomplete;

    return () => {
      if (listener) window.google.maps.event.removeListener(listener);
      autocompleteRef.current = null;
    };
  }, [ready]);

  return (
    <div className="relative">
      <input
        ref={inputRef}
        id={id}
        type="text"
        required={required}
        autoComplete="off"
        data-testid={testId}
        className={className}
        placeholder={placeholder}
        value={value}
        onChange={(e) =>
          onChange({
            address: e.target.value,
            lat: null,
            lng: null,
            place_id: null,
          })
        }
      />
      {!ready && !loadError && (
        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-zinc-400 pointer-events-none" />
      )}
      {loadError && (
        <p className="text-xs text-amber-600 mt-1">
          Autocomplete nuk u ngarkua — shkruani adresën ose emrin e biznesit manualisht.
        </p>
      )}
    </div>
  );
};

export default PlaceAutocompleteInput;
